import isObject from 'lodash/isObject'

import { redirectOnUnauthorized } from '@src/auth/unauthorized-server.util'
import { rethrowControlFlow } from '@src/utils/error.util'
import { JSendError, JSendFailure, type JSendResponse, isJSend } from '@src/utils/jsend.util'
import { log, redactSensitiveFields, truncateLargeValues } from '@src/utils/log.util'
import { clone } from '@src/utils/object.util'

type AsyncFn = (...args: any[]) => Promise<any>

/**
 * Strip JSendResponse from a function's return type.
 * Because instrumentAction throws JSend fail/error responses, callers never receive them —
 * this mapped type reflects that at the type level.
 */
type InstrumentedAction<T extends AsyncFn> = (
  ...args: Parameters<T>
) => Promise<Exclude<Awaited<ReturnType<T>>, JSendResponse>>

/**
 * Wraps an async function with structured logging, sensitive-arg/result redaction,
 * and JSend fail/error throw normalization.
 *
 * Applied at the service layer so RSCs, Route Handlers, and Server Actions all
 * share identical instrumentation with a single source of truth.
 */
export function instrumentAction<T extends AsyncFn>(name: string, fn: T): InstrumentedAction<T> {
  return async function (...args: Parameters<T>) {
    const startTime = Date.now()

    try {
      const redactedArgs = (clone(args as unknown[]) as unknown[]).map((arg) =>
        isObject(arg) ? redactSensitiveFields(arg) : '[REDACTED]'
      )

      log.info(`⏳ ${name}`, { args: redactedArgs })

      // Services catch their own request errors, so this is the only place that can turn
      // an upstream 401 into a login redirect.
      const result = await redirectOnUnauthorized(name, () => fn(...args))

      if (isJSend(result)) {
        if (result.status === 'fail') throw new JSendFailure(result)
        if (result.status === 'error') throw new JSendError(result)
      }

      log.info(`✅ ${name}`, {
        result:
          typeof result === 'string'
            ? '[REDACTED]'
            : isObject(result)
              ? truncateLargeValues(redactSensitiveFields(clone(result)))
              : result,
        duration: `${Date.now() - startTime}ms`,
      })

      return result as Exclude<Awaited<ReturnType<T>>, JSendResponse>
    } catch (error: any) {
      rethrowControlFlow(error)
      log.error(`❌ ${name} - ${error?.message}`, error)
      throw error
    }
  } as unknown as InstrumentedAction<T>
}

type ServiceMethods = Record<string, AsyncFn | unknown>

type InstrumentedService<T extends ServiceMethods> = {
  [K in keyof T]: T[K] extends AsyncFn ? InstrumentedAction<T[K]> : T[K]
}

/**
 * Maps `instrumentAction` over every async-function property of a service object.
 * Sync functions and non-function properties (api clients, etc.) are passed through unchanged,
 * so callers that invoke sync methods are not affected.
 *
 * @example
 * const UserService = instrumentService('UserService', userServiceImpl)
 */
export function instrumentService<T extends ServiceMethods>(
  namespace: string,
  service: T
): InstrumentedService<T> {
  const instrumented = Object.create(null) as InstrumentedService<T>

  for (const key of Object.keys(service) as (keyof T)[]) {
    const value = service[key]
    const isAsyncFn = typeof value === 'function' && value.constructor.name === 'AsyncFunction'
    if (isAsyncFn) {
      const name = `${namespace}.${String(key)}`
      ;(instrumented as any)[key] = instrumentAction(name, (value as AsyncFn).bind(service))
    } else {
      ;(instrumented as any)[key] = value
    }
  }

  return instrumented
}
